import React from 'react'

import { Box, Chip, IconButton, Tooltip, Typography } from '@material-ui/core'
import { first, last } from 'lodash-es'
import {
  Map as MapContainer,
  Polyline,
  TileLayer,
  Marker,
  Tooltip as MapTooltip,
  ZoomControl,
  Popup,
} from 'react-leaflet'
import { ReactSVG } from 'react-svg'

import LiveMarker from '~/components/LiveMarker'
import { mapUrl } from '~/config'
import { iconStatusMapping } from '~/config/constants'
import { firstTripIcon } from '~/config/iconMap'
import theme from '~/styles/theme'

import useCustom from './hooks'
import useStyle from './style'

const DetailMapPublic = () => {
  const { data, methods } = useCustom()
  const trips = data?.trips || []
  const firstCenter = first(trips)
  const lastCenter = last(trips)
  const currentStatus =
    iconStatusMapping[data.lastData?.current?.status.toLowerCase()]
  const classes = useStyle()
  if (!data.isAnyData)
    return (
      <Box
        height="100vh"
        width="100vw"
        bgcolor="white"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Box fontSize={30}>
          {data.loading ? 'Loading...' : 'No data available'}
        </Box>
      </Box>
    )

  if (!firstCenter || !lastCenter || trips.length === 0) {
    return (
      <Box
        height="100vh"
        width="100vw"
        bgcolor="white"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Box fontSize={30}>Location unavailable</Box>
      </Box>
    )
  }

  if (data.remainingSecs === 0) {
    return (
      <Box height="100vh" width="100vw" overflow="hidden">
        <Box className={classes.navbar}>
          <Typography className={classes.navbarTitle}>LIVE TRACKING</Typography>
          <Chip
            label="Expired"
            className={`${classes.expiryChip} ${classes.expiryChipExpired}`}
            size="medium"
          />
        </Box>
        <Box className={classes.expiredScreen}>
          <Box className={classes.expiredIcon}>🔒</Box>
          <Typography className={classes.expiredTitle}>
            This link has expired
          </Typography>
          <Typography className={classes.expiredSubtitle}>
            The live tracking session for this vehicle is no longer active.
            <br />
            Please request a new link to continue tracking.
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <Box height="100vh" width="100vw" overflow="hidden">
      <Box className={classes.navbar}>
        <Typography className={classes.navbarTitle}>LIVE TRACKING</Typography>
        {data.expiryLabel && (
          <Chip
            label={data.expiryLabel}
            className={`${classes.expiryChip} ${
              data.remainingSecs === 0 ? classes.expiryChipExpired : ''
            }`}
            size="medium"
          />
        )}
      </Box>
      <Box className={classes.mapWrapper}>
        <MapContainer
          center={lastCenter}
          zoom={17}
          zoomControl={false}
          className={classes.mapContainer}
        >
          <TileLayer
            url={mapUrl}
            attribution='<a href="https://www.maptiler.com/copyright/" target="_blank">&copy; MapTiler</a> <a href="https://www.openstreetmap.org/copyright" target="_blank">&copy; OpenStreetMap contributors</a>'
          />
          <ZoomControl position="bottomright" />
          <LiveMarker
            key={`start-marker-${data.searchQuery.id}-${
              data.detailData?.vehicleName || ''
            }`}
            position={firstCenter}
            icon={trips.length <= 1 ? currentStatus : firstTripIcon}
            rotationAngle={trips.length <= 1 ? 0 : data.detailData.direction}
            duration={1000}
          >
            {trips.length <= 1 && (
              <MapTooltip
                direction="top"
                offset={[1.75, 15]}
                opacity={1}
                permanent
              >
                {data.detailData?.vehicleName || ''}
              </MapTooltip>
            )}
            <Popup position={firstCenter}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>{data.detailData?.vehicleName || ''}</Box>
                <Box>
                  <Tooltip title="Google Maps" placement="top" arrow>
                    <IconButton
                      onClick={() =>
                        methods.handleClickGoogleMapsShare(
                          firstCenter?.[0] || 0,
                          firstCenter?.[1] || 0
                        )
                      }
                      size="small"
                      className={classes.iconButtonShareMap}
                    >
                      <ReactSVG
                        className={classes.filterIcon}
                        src="/public/icons/google-maps.svg"
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Popup>
          </LiveMarker>
          {trips.length > 1 && (
            <>
              <Polyline
                positions={trips as any}
                color={theme.palette.primary.light}
              />
              {trips.slice(1, trips.length).map((trip, index) => {
                const tripsLen = trips.length
                const tripNumber = index + 1
                if (tripNumber !== tripsLen - 1) {
                  return (
                    <Marker
                      key={`marker-${data.searchQuery.id}-${data.detailData?.vehicleName}-${tripNumber}`}
                      position={trip as any}
                      rotationAngle={data.lastData?.current?.direction}
                      icon={currentStatus}
                      opacity={0}
                    />
                  )
                }
                return (
                  <LiveMarker
                    key={`marker-${data.searchQuery.id}-${data.detailData?.vehicleName}-${tripNumber}`}
                    position={trip as any}
                    rotationAngle={data.lastData?.current?.direction - 180}
                    icon={currentStatus}
                    opacity={1}
                    duration={1000}
                  >
                    <MapTooltip
                      direction="top"
                      offset={[1.75, 15]}
                      opacity={1}
                      permanent
                    >
                      {data.detailData?.vehicleName || ''}
                    </MapTooltip>
                  </LiveMarker>
                )
              })}
            </>
          )}
        </MapContainer>
      </Box>
    </Box>
  )
}

export default DetailMapPublic
